// First, before anything that builds a zod schema — see the module for why.
import './config/zod'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { App } from './app/App'
import { env } from './config/env'
import './styles/index.css'

async function bootstrap() {
  // There is no documented DHA sandbox, so the mock backend is how this app runs
  // locally and how Playwright drives it deterministically. Started before the
  // first render so no request can escape while the worker is still registering.
  if (env.enableMsw) {
    // Aliased to a stub in builds where MSW is off, which is what keeps the mock
    // backend out of the production artefact. See resolve.alias in vite.config.ts.
    const { startMockBackend } = await import('@/mocks/browser')
    await startMockBackend()
  }

  // No token wiring here, and no token at all. There was a two-token chain — mint a
  // VFS token, trade it for a DHA one, call DHA directly — and §4.8 removed the need
  // for it: VFS's backend calls DHA on our behalf and holds that token itself. The
  // browser's own credential is a session cookie the backend issues on first use
  // (lib/http/vfsclient/session.ts), invisible to JavaScript, so there is nothing to
  // install before the first render.

  // The officer signs in through a full-page redirect to Microsoft, and the
  // authorization code comes back in the URL fragment. This has to run — and be
  // awaited — before the first render: React Router reads `location` on mount and
  // would navigate away, dropping the fragment, before MSAL ever saw it.
  //
  // It never rejects. A throw here would abort bootstrap() and leave a blank page
  // instead of an error the officer can act on; the failure is surfaced by the
  // Officer Login screen instead.
  if (env.officerSso) {
    // The `@/` form, not a relative path: that is the specifier vite.config.ts
    // aliases to the stub when the flag is off, exactly as it does for @/mocks/browser.
    const { initOfficerSso } = await import('@/lib/auth/officerSso')
    await initOfficerSso()
  }

  // The client-side audit sink is gone too: §4.8 logs every DHA call server-side,
  // so there is no longer a seam to wire the wizard session into.

  const container = document.getElementById('root')
  if (!container) throw new Error('Root element #root was not found.')

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>
  )
}

void bootstrap()
